// src/store/storageStore.js
//
// Zustand store for storage usage — org-wide AND per-user.
// Backend now returns both levels in one response from GET /file/storage-usage/
//
// Usage:
//   import { useStorageStore } from "../store/storageStore";

import { create } from "zustand";
import { api } from "../utils/axios-instance";

export const useStorageStore = create((set, get) => ({
  // ── Org-level state ────────────────────────────────────────────────────────
  usedBytes: 0,
  quotaBytes: 50 * 1024 ** 3, // 100 GB safe default until first fetch
  usedFormatted: "0 B",
  quotaFormatted: "50 GB",
  percent: 0,
  plan: "starter",
  seats: 1,
  isOverQuota: false,

  // ── User-level state (actual bytes this user uploaded) ────────────────────
  userUsedBytes: 0,
  userQuotaBytes: 50 * 1024 ** 3,
  userUsedFormatted: "0 B",
  userQuotaFormatted: "50 GB",
  userPercent: 0,
  userIsOverQuota: false,

  // ── Meta ───────────────────────────────────────────────────────────────────
  loading: true,
  error: null,
  lastFetched: null,

  // ── Fetch from backend ─────────────────────────────────────────────────────
  fetchStorageUsage: async () => {
    console.trace("fetchStorageUsage invoked");
    try {
      const res = await api.get("/file/storage-usage/");
      const d = res.data;

      set({
        // Org-level
        usedBytes: d.used_bytes,
        quotaBytes: d.quota_bytes,
        usedFormatted: d.used_formatted,
        quotaFormatted: d.quota_formatted,
        percent: d.percent,
        plan: d.plan,
        seats: d.seats,
        isOverQuota: d.is_over_quota,

        // User-level — real per-user bytes from Firestore, not a math estimate
        userUsedBytes: d.user_used_bytes,
        userQuotaBytes: d.user_quota_bytes,
        userUsedFormatted: d.user_used_formatted,
        userQuotaFormatted: d.user_quota_formatted,
        userPercent: d.user_percent,
        userIsOverQuota: d.user_is_over_quota,

        loading: false,
        error: null,
        lastFetched: Date.now(),
      });
    } catch (e) {
      console.error("Storage usage fetch failed:", e);
      set({ loading: false, error: e?.message || "Failed to fetch storage" });
    }
  },

  // ── Optimistic local update after confirmed upload/delete ─────────────────
  // deltaBytes > 0 on upload, < 0 on delete.
  // Updates BOTH org and user counters so both bars reflect the change instantly.
  adjustLocal: (deltaBytes) => {
    set((s) => {
      // Org counter
      const newOrgUsed = Math.max(0, s.usedBytes + deltaBytes);
      const orgPct = s.quotaBytes
        ? Math.min((newOrgUsed / s.quotaBytes) * 100, 100)
        : 0;

      // User counter
      const newUserUsed = Math.max(0, s.userUsedBytes + deltaBytes);
      const userPct = s.userQuotaBytes
        ? Math.min((newUserUsed / s.userQuotaBytes) * 100, 100)
        : 0;

      return {
        usedBytes: newOrgUsed,
        usedFormatted: _fmtBytes(newOrgUsed),
        percent: Math.round(orgPct * 10) / 10,
        isOverQuota: newOrgUsed > s.quotaBytes,

        userUsedBytes: newUserUsed,
        userUsedFormatted: _fmtBytes(newUserUsed), // ← fixes stale pill text
        userPercent: Math.round(userPct * 10) / 10,
        userIsOverQuota: newUserUsed > s.userQuotaBytes,
      };
    });
  },

  // ── User quota block — true when user's personal seat quota is ≥ 98% ─────
  // Read this before opening upload dialogs to block at the UI level.
  // This is separate from the org-level checkQuota check.
  isUserStorageBlocked: () => {
    const { userPercent } = get();
    return userPercent >= 98;
  },

  // ── Quota guard — call before upload to show early error ──────────────────
  // Checks against the ORG quota (same enforcement as the backend).
  // Returns { allowed: bool, message: string | null }
  checkQuota: (incomingBytes = 0) => {
    const { usedBytes, quotaBytes, quotaFormatted } = get();
    const remaining = quotaBytes - usedBytes;

    if (incomingBytes > 0 && incomingBytes > remaining) {
      const remFmt = _fmtBytes(remaining);
      return {
        allowed: false,
        message: `Not enough storage. ${remFmt} remaining of ${quotaFormatted}.`,
      };
    }
    return { allowed: true, message: null };
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Internal helper — mirrors backend _fmt_bytes()
// ─────────────────────────────────────────────────────────────────────────────
function _fmtBytes(b) {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  b = Math.max(0, b);
  while (b >= 1024 && i < units.length - 1) {
    b /= 1024;
    i++;
  }
  return `${b.toFixed(1)} ${units[i]}`;
}
